// hooks/useDeviationAutoSave.ts
import { useDeviation } from '@cdab/scania/qpr/contexts/deviation'
import type { DeviationModel } from '@cdab/scania/qpr/offline/models'
import type { DeviationValidationSchemaDraftOut } from '@cdab/scania/qpr/schema'
import { DeviationValidationSchemaDraft } from '@cdab/scania/qpr/schema'
import { useCallback, useEffect, useRef, useState } from 'react'

export const useDeviationAutoSave = (
  deviationCreated: boolean,
  deviation: DeviationModel,
  updateCreateDraftDeviation: (
    data: DeviationValidationSchemaDraftOut
  ) => Promise<void>
) => {
  const ctx = useDeviation()
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  const [parsedData, setParsedData] = useState<
    DeviationValidationSchemaDraftOut | undefined
  >(undefined)

  const cancelAutosave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = undefined
    }
  }, [])

  useEffect(() => {
    if (!ctx.form) return

    let lastFormData: string | null = null

    const subscription = ctx.form.watch(async formData => {
      const currentFormDataString = JSON.stringify(formData)
      if (currentFormDataString === lastFormData) return

      lastFormData = currentFormDataString

      let parsedData: DeviationValidationSchemaDraftOut | undefined
      try {
        parsedData = DeviationValidationSchemaDraft.parse({
          ...formData,
          uploadedFiles: deviation.files
        })
        setParsedData(parsedData)
      } catch (error) {
        parsedData = undefined
        setParsedData(undefined)
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        if (formData && parsedData) {
          await updateCreateDraftDeviation({ ...parsedData, draft: true })
        }
      }, 1000)
    })

    return () => subscription?.unsubscribe()
  }, [ctx.form, updateCreateDraftDeviation, deviation.files])

  return { parsedData, cancelAutosave }
}
